// src/services/sessionService.js
import { logoutService } from './logoutService';

class SessionService {
     constructor () {
          // this.sessionTimeout = 30 * 60 * 1000; // 30 minutes default
          // this.warningTime = 5 * 60 * 1000; // 5 minutes warning
         // this.sessionTimeout = (process.env.REACT_APP_SESSION_TIMEOUT_MINUTES || 3) * 60 * 1000;
          this.sessionTimeout = 2 * 60 * 60 * 1000; // 2 hours
          this.warningTime = (process.env.REACT_APP_SESSION_WARNING_MINUTES || 2) * 60 * 1000;

          this.checkInterval = 10 * 1000; // Check every 10 seconds for better accuracy
          this.lastActivity = Date.now();
          this.isActive = false;
          this.timer = null;
          this.warningTimer = null;
          this.onSessionExpired = null;
          this.onSessionWarning = null;
     }

     /**
      * Initialize session monitoring
      * @param {Object} options - Configuration options
      * @param {number} options.timeout - Session timeout in milliseconds
      * @param {number} options.warningTime - Warning time before expiry in milliseconds
      * @param {Function} options.onExpired - Callback when session expires
      * @param {Function} options.onWarning - Callback when warning is triggered
      */
     init(options = {}) {
          // Stop any existing tracking to prevent duplicates
          this.stopTracking();

          this.sessionTimeout = options.timeout || this.sessionTimeout;
          this.warningTime = options.warningTime || this.warningTime;
          this.onSessionExpired = options.onExpired || this.defaultExpiredHandler;
          this.onSessionWarning = options.onWarning || this.defaultWarningHandler;

          this.startTracking();
          console.log('Session monitoring initialized');
     }

     /**
      * Start tracking user activity
      */
     startTracking() {
          if (this.isActive) return;

          this.isActive = true;
          this.lastActivity = Date.now();

          // Set up activity event listeners
          const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

          events.forEach(event => {
               document.addEventListener(event, this.updateActivity.bind(this), { passive: true });
          });

          // Start the session check timer
          this.startTimer();

          // Store last activity in localStorage for cross-tab sync
          localStorage.setItem('lastActivity', this.lastActivity.toString());
     }

     /**
      * Stop tracking user activity
      */
     stopTracking() {
          if (!this.isActive) return;

          this.isActive = false;

          // Remove event listeners
          const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

          events.forEach(event => {
               document.removeEventListener(event, this.updateActivity.bind(this));
          });

          // Clear timers
          this.clearTimers();

          console.log('Session monitoring stopped');
     }

     /**
      * Update last activity timestamp
      */
     updateActivity() {
          this.lastActivity = Date.now();
          localStorage.setItem('lastActivity', this.lastActivity.toString());

          // Reset timers on activity
          this.clearTimers();
          this.startTimer();
     }

     /**
      * Start the session timer
      */
     startTimer() {
          this.timer = setInterval(() => {
               this.checkSession();
          }, this.checkInterval);
     }

     /**
      * Clear all timers
      */
     clearTimers() {
          if (this.timer) {
               clearInterval(this.timer);
               this.timer = null;
          }

          if (this.warningTimer) {
               clearTimeout(this.warningTimer);
               this.warningTimer = null;
          }
     }

     /**
      * Check session status
      */
     checkSession() {
          // Check cross-tab activity
          const storedActivity = localStorage.getItem('lastActivity');
          if (storedActivity) {
               this.lastActivity = Math.max(this.lastActivity, parseInt(storedActivity));
          }

          const now = Date.now();
          const timeSinceActivity = now - this.lastActivity;
          const timeUntilExpiry = this.sessionTimeout - timeSinceActivity;
          console.log(timeUntilExpiry);

          // Debug logging
          const minutesSinceActivity = Math.floor(timeSinceActivity / (60 * 1000));
          const secondsUntilExpiry = Math.floor(timeUntilExpiry / 1000);

          console.log(`⏱️ Session check: ${minutesSinceActivity}m since activity, ${secondsUntilExpiry}s until expiry`);

          // Check if session has expired
          if (timeSinceActivity >= this.sessionTimeout) {
               console.log('🔴 Session timeout reached - triggering expiration');
               this.handleSessionExpired();
               return;
          }

          // Check if warning should be shown
          if (timeUntilExpiry <= this.warningTime && !this.warningTimer) {
               console.log('⚠️ Session warning threshold reached');
               this.handleSessionWarning(Math.floor(timeUntilExpiry / 1000));
          }
     }

     /**
      * Handle session expiration
      */
     handleSessionExpired() {
          console.log('🔴 Session expired due to inactivity');
          this.stopTracking();

          if (this.onSessionExpired) {
               console.log('📞 Calling custom session expired handler');
               this.onSessionExpired();
          } else {
               console.log('📞 Calling default session expired handler');
               this.defaultExpiredHandler();
          }
     }

     /**
      * Handle session warning
      */
     handleSessionWarning(secondsLeft) {
          console.log(`Session warning: ${secondsLeft} seconds left`);

          if (this.onSessionWarning) {
               this.onSessionWarning(secondsLeft);
          } else {
               this.defaultWarningHandler(secondsLeft);
          }

          // Set timer to expire session if no activity
          this.warningTimer = setTimeout(() => {
               this.handleSessionExpired();
          }, secondsLeft * 1000);
     }

     /**
      * Default session expired handler
      */
     defaultExpiredHandler() {
          console.log('🔴 Session expired - starting logout process...');

          // Perform logout and redirect
          logoutService.performCompleteLogout({
               redirectToLogout: false,
               clearAllData: true
          }).then(() => {
               console.log('✅ Logout completed - redirecting to login page');
               window.location.replace('/?type=session_expired&reason=inactivity');
          }).catch((error) => {
               console.error('❌ Logout failed - performing emergency redirect:', error);
               window.location.replace('/?type=emergency&reason=session_timeout');
          });
     }

     /**
      * Default session warning handler
      */
     defaultWarningHandler(secondsLeft) {
          const minutes = Math.floor(secondsLeft / 60);
          const seconds = secondsLeft % 60;

          const message = minutes > 0
               ? `Your session will expire in ${minutes}:${seconds.toString().padStart(2, '0')} minutes due to inactivity.`
               : `Your session will expire in ${seconds} seconds due to inactivity.`;

          // Show browser notification if permission is granted
          if (Notification.permission === 'granted') {
               new Notification('Session Warning', {
                    body: message,
                    icon: '/favicon.ico'
               });
          }

          // Don't use alert as it blocks the UI - let the SessionProvider modal handle this
          console.log('Session Warning:', message);

          // User interaction resets the session (this will be handled by the modal buttons)
          // this.updateActivity(); // Removed - let user explicitly continue session
     }

     /**
      * Extend session manually
      */
     extendSession() {
          this.updateActivity();
          console.log('Session extended manually');
     }

     /**
      * Get remaining session time
      */
     getRemainingTime() {
          const now = Date.now();
          const timeSinceActivity = now - this.lastActivity;
          const remaining = this.sessionTimeout - timeSinceActivity;

          return Math.max(0, remaining);
     }

     /**
      * Get session info
      */
     getSessionInfo() {
          const remaining = this.getRemainingTime();
          const minutes = Math.floor(remaining / (60 * 1000));
          const seconds = Math.floor((remaining % (60 * 1000)) / 1000);

          return {
               isActive: this.isActive,
               lastActivity: new Date(this.lastActivity),
               remainingMs: remaining,
               remainingMinutes: minutes,
               remainingSeconds: seconds,
               isExpired: remaining <= 0
          };
     }

     /**
      * Request notification permission
      */
     async requestNotificationPermission() {
          if ('Notification' in window) {
               const permission = await Notification.requestPermission();
               console.log('Notification permission:', permission);
               return permission === 'granted';
          }
          return false;
     }

     /**
      * Check if user is authenticated
      */
     isAuthenticated() {
          const token = localStorage.getItem('authToken');
          const exp = localStorage.getItem('exp');

          if (!token || !exp) {
               return false;
          }

          // Check if token is expired
          const expirationTime = parseInt(exp);
          const now = Date.now();

          return now < expirationTime;
     }
}

// Export singleton instance
export const sessionService = new SessionService();
export default SessionService;
